import React, { useState, useEffect } from 'react';
import '../styles/InstallPrompt.css';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return;
      }

      // Check for iOS Safari
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        return;
      }

      // Check if prompt was dismissed
      const promptDismissed = localStorage.getItem('install-prompt-dismissed');
      if (promptDismissed) {
        const dismissedTime = parseInt(promptDismissed);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

        // Show again after 7 days
        if (daysSinceDismissed < 7) {
          return;
        }
      }

      setShowPrompt(true);
    };

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (event) => {
      console.log('beforeinstallprompt event fired');
      event.preventDefault();
      setDeferredPrompt(event);
      checkIfInstalled();
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    checkIfInstalled();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    console.log('[InstallPrompt] Install button clicked');

    // Try global PWA installer first
    if (window.triggerPWAInstall) {
      window.triggerPWAInstall();
      setShowPrompt(false);
      return;
    }

    if (!deferredPrompt) {
      // Show manual install instructions for iOS/other browsers
      setShowPrompt(false);
      showManualInstallInstructions();
      return;
    }

    try {
      console.log('[InstallPrompt] Triggering install prompt');

      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`[InstallPrompt] User response: ${outcome}`);

      if (outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
      } else {
        console.log('❌ User dismissed the install prompt');
        localStorage.setItem('install-prompt-dismissed', Date.now().toString());
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('[InstallPrompt] Error during installation:', error);
      // Fallback to manual instructions
      showManualInstallInstructions();
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('install-prompt-dismissed', Date.now().toString());
  };

  const showManualInstallInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    let instructions = '';

    if (isIOS) {
      instructions = 'To install this app on iOS:\n1. Tap the Share button in Safari\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm';
    } else if (isAndroid) {
      instructions = 'To install this app on Android:\n1. Tap the menu (⋮) in your browser\n2. Select "Add to Home screen"\n3. Tap "Add" to confirm';
    } else {
      instructions = 'To install this app:\n1. Look for an install icon in your browser\'s address bar\n2. Click it and follow the prompts\n3. Or use your browser\'s menu to "Install" or "Add to Home Screen"';
    }

    alert(instructions);
  };

  // Don't show if already installed
  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className="install-prompt">
      <div className="install-prompt-content">
        <div className="install-prompt-icon">📱</div>
        <div className="install-prompt-text">
          <h3>Install Travel Companion</h3>
          <p>Get quick access to your travel plans and notes</p>
        </div>
        <div className="install-prompt-buttons">
          <button
            className="install-button"
            onClick={handleInstallClick}
          >
            Install
          </button>
          <button
            className="dismiss-button"
            onClick={handleDismiss}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;